// M02 /diagnosticos/:id/avaliacao/:scenario: consensus answers per question, one category at a time, with explicit
// save, the finalize confirmation, frozen read-only state and, for the prognosis, the diagnosis answer as reference.
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronLeft, ChevronRight, MessageSquareText, Undo2, UserRound } from "lucide-react";
import { Alert, Button, ErrorState, Panel, Pill, SelectField, Skeleton, TextArea, TextField, cx } from "../../components/ui.tsx";
import { Modal } from "../../components/dialog.tsx";
import { StickyActions } from "../../components/nav-controls.tsx";
import { GuardedLink } from "../../components/plan-shell.tsx";
import { useToast } from "../../components/toast.tsx";
import { ApiError, get, post, put } from "../../lib/api.ts";
import { useUnsavedGuard } from "../../lib/guard.tsx";
import { useUrlState } from "../../lib/nav.tsx";
import {
  CONFLICT_MESSAGE, PROGNOSIS_NOTE, SCENARIO_LABEL, SCENARIO_TAGLINE, mBase, mKey, useWorkflow, withInstitution,
  type AssessmentDetail, type MaturityInstitution, type ScenarioDetail, type ScenarioKind, type WorkflowAssignment,
} from "../../lib/maturity.ts";
import { CATEGORY_STATE_LABEL, CATEGORY_STATE_TONE, NOTE_MAX, OPEN_STATE, type CategoryStateInfo } from "../../../shared/maturity/workflow.ts";
import { AssessmentShell } from "./AssessmentShell.tsx";
import { AnswerOptions } from "../../components/maturity/answer-options.tsx";
import { WithInstitution } from "./MaturityLayout.tsx";
import { formatInstantBR } from "../../../shared/dates.ts";
import { MATURITY_LIMITS, questionsOf, type DefCategory, type DefQuestion } from "../../../shared/maturity/definition.ts";
import { EMPTY_ENTRY, canonical, type Answer, type ResponseEntry, type Responses } from "../../../shared/maturity/responses.ts";
import { ANSWER_LABEL, NO_ANSWER_LABEL, computeScores, formatPercent, type ScoreSummary } from "../../../shared/maturity/scoring.ts";

const scenarioKey = (inst: string, id: string, kind: ScenarioKind) => mKey(inst, "assessment", id, "scenario", kind);

export function ScenarioPage({ id, kind }: { id: string; kind: ScenarioKind }) {
  return (
    <WithInstitution>
      {(inst) => (
        <AssessmentShell institution={inst} id={id} tab={kind} title={SCENARIO_LABEL[kind]}>
          {(a) => <ScenarioBody key={`${a.id}-${kind}`} institution={inst} assessment={a} kind={kind} />}
        </AssessmentShell>
      )}
    </WithInstitution>
  );
}

function useScenario(inst: string, id: string, kind: ScenarioKind, enabled: boolean) {
  return useQuery({
    queryKey: scenarioKey(inst, id, kind),
    queryFn: async () => (await get<{ data: ScenarioDetail }>(`${mBase(inst)}/assessments/${id}/scenarios/${kind}`)).data,
    enabled,
  });
}

function ScenarioBody({ institution, assessment: a, kind }: { institution: MaturityInstitution; assessment: AssessmentDetail; kind: ScenarioKind }) {
  const exists = Boolean(a.scenarios[kind]);
  const q = useScenario(institution.id, a.id, kind, exists);
  const reference = useScenario(institution.id, a.id, "diagnosis", kind === "prognosis" && exists);
  if (!exists) return <StartPrognosis institution={institution} assessment={a} />;
  if (q.isPending || (kind === "prognosis" && reference.isPending)) return <Skeleton lines={8} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return <ScenarioForm key={`${q.data.id}:${q.data.version}`} institution={institution} assessment={a} scenario={q.data} reference={kind === "prognosis" ? (reference.data ?? null) : null} />;
}

function StartPrognosis({ institution, assessment: a }: { institution: MaturityInstitution; assessment: AssessmentDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (!a.capabilities.can_start_prognosis) {
    return (
      <Alert tone="info" title="O prognóstico começa depois do diagnóstico.">
        Encerre o diagnóstico para registrar, com as mesmas perguntas, como a instituição poderá ficar se o plano de gestão for bem-sucedido.
        <div className="mt-2"><GuardedLink className="font-semibold text-primary underline" href={withInstitution(`/diagnosticos/${a.id}/avaliacao/diagnosis`, institution.id)}>Ir para o diagnóstico</GuardedLink></div>
      </Alert>
    );
  }
  async function start() {
    setBusy(true);
    try {
      await post(`${mBase(institution.id)}/assessments/${a.id}/prognosis`);
      await qc.invalidateQueries({ queryKey: mKey(institution.id) });
      toast("success", "Prognóstico iniciado. As respostas começam em branco.");
    } catch (e) {
      toast("danger", e instanceof Error ? e.message : "Não foi possível iniciar o prognóstico.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Panel className="max-w-2xl">
      <p className="eyebrow">{SCENARIO_TAGLINE.prognosis}</p>
      <h2 className="mt-1 text-xl font-bold">Iniciar prognóstico</h2>
      <p className="mt-2 text-sm text-muted">{PROGNOSIS_NOTE} O grupo responde as mesmas {questionsOf(a.definition).length} perguntas pensando no cenário futuro. As respostas do diagnóstico aparecem ao lado como referência e não mudam.</p>
      <Button className="mt-4" loading={busy} onClick={() => void start()}>Iniciar prognóstico</Button>
    </Panel>
  );
}

const sameBuffer = (a: Responses, b: Responses) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
const sameEntry = (a: ResponseEntry | undefined, b: ResponseEntry | undefined) => JSON.stringify(canonical({ x: a ?? EMPTY_ENTRY })) === JSON.stringify(canonical({ x: b ?? EMPTY_ENTRY }));

function ScenarioForm({ institution, assessment: a, scenario: s, reference }: {
  institution: MaturityInstitution; assessment: AssessmentDetail; scenario: ScenarioDetail; reference: ScenarioDetail | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const frozen = s.state === "finalized";
  const [buffer, setBuffer] = useState<Responses>(() => structuredClone(s.responses));
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [pendingNotice, setPendingNotice] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [unapprovedNotice, setUnapprovedNotice] = useState<string[] | null>(null);
  const [returning, setReturning] = useState(false);
  const finalizeKey = useRef<string | null>(null);
  const workflow = useWorkflow(institution.id, a.id);
  const assignments = workflow.data?.assignments ?? {};
  const stateOf = (catId: string): CategoryStateInfo => s.categories?.[catId] ?? OPEN_STATE;
  const categories = useMemo(() => a.definition.dimension.subdimensions.flatMap((sd) => sd.categories.map((c) => ({ sub: sd, cat: c }))), [a.definition]);
  const [url, setUrl] = useUrlState({ cat: categories[0]?.cat.id ?? "" });
  const index = Math.max(0, categories.findIndex((c) => c.cat.id === url.cat));
  const current = categories[index]!;
  const dirty = !frozen && !sameBuffer(buffer, s.responses);
  useUnsavedGuard(dirty);

  const scores = useMemo(() => computeScores(a.definition, (id) => buffer[id]?.answer ?? null), [a.definition, buffer]);
  const catScore = (c: DefCategory) => scores.subdimensions.flatMap((x) => x.categories).find((x) => x.id === c.id)!;
  const allIds = useMemo(() => questionsOf(a.definition).map((q) => q.id), [a.definition]);
  const fullBuffer = () => Object.fromEntries(allIds.map((id) => [id, buffer[id] ?? EMPTY_ENTRY]));
  const pending = allIds.filter((id) => !buffer[id]?.answer);
  const catOfQuestion = useMemo(() => new Map(categories.flatMap((c) => c.cat.questions.map((q) => [q.id, c.cat.id] as const))), [categories]);
  // BLD-28: assigned categories must be approved before closing.
  const unapproved = categories.filter((c) => assignments[c.cat.id] && stateOf(c.cat.id).state !== "approved").map((c) => c.cat.id);

  const setEntry = (qid: string, patch: Partial<ResponseEntry>) => {
    setBuffer((b) => ({ ...b, [qid]: { ...(b[qid] ?? EMPTY_ENTRY), ...patch } }));
    setPendingNotice(null);
  };
  const path = `${mBase(institution.id)}/assessments/${a.id}/scenarios/${s.kind}`;

  async function refresh() {
    await qc.invalidateQueries({ queryKey: mKey(institution.id, "assessment", a.id) });
    await qc.invalidateQueries({ queryKey: mKey(institution.id, "assessments") });
  }
  function onError(e: unknown, fallback: string) {
    if (e instanceof ApiError && e.code === "VERSION_CONFLICT") setConflict(true);
    else if (e instanceof ApiError && e.code === "CATEGORIES_NOT_APPROVED") setUnapprovedNotice(((e.details?.category_ids as string[] | undefined) ?? unapproved));
    else toast("danger", e instanceof Error ? e.message : fallback);
  }
  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      // Only changed entries, checked per category, so participants answering other categories never conflict.
      const changed = Object.fromEntries(allIds.filter((q) => !sameEntry(buffer[q], s.responses[q])).map((q) => [q, buffer[q] ?? EMPTY_ENTRY]));
      const touched = [...new Set(Object.keys(changed).map((q) => catOfQuestion.get(q)!))];
      const res = await put<{ data: ScenarioDetail }>(path, {
        expected_version: s.version, responses: changed, category_versions: Object.fromEntries(touched.map((c) => [c, stateOf(c).version])),
      });
      qc.setQueryData(scenarioKey(institution.id, a.id, s.kind), res.data);
      await refresh();
      toast("success", "Rascunho salvo.");
    } catch (e) {
      onError(e, "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  }
  function askFinalize() {
    if (pending.length) {
      setPendingNotice(pending.length);
      return;
    }
    if (unapproved.length) {
      setUnapprovedNotice(unapproved);
      return;
    }
    finalizeKey.current = crypto.randomUUID();
    setConfirming(true);
  }
  async function finalize() {
    setSaving(true);
    try {
      const res = await post<{ data: ScenarioDetail }>(`${path}/finalize`, { expected_version: s.version, responses: fullBuffer() }, { "Idempotency-Key": finalizeKey.current! });
      setConfirming(false);
      qc.setQueryData(scenarioKey(institution.id, a.id, s.kind), res.data);
      await refresh();
      toast("success", s.kind === "diagnosis" ? "Diagnóstico encerrado e preservado." : "Prognóstico encerrado. A comparação está completa no painel.");
    } catch (e) {
      setConfirming(false);
      onError(e, "Não foi possível encerrar.");
    } finally {
      setSaving(false);
    }
  }
  const goToFirstPending = () => {
    const c = categories.find((x) => x.cat.questions.some((q) => !buffer[q.id]?.answer));
    if (c) setUrl({ cat: c.cat.id });
    setPendingNotice(null);
  };
  const cs = catScore(current.cat);
  const currentAssignment = assignments[current.cat.id];
  const currentState = stateOf(current.cat.id);
  const categoryDirty = current.cat.questions.some((q) => !sameEntry(buffer[q.id], s.responses[q.id]));
  const categorySavedComplete = current.cat.questions.every((q) => s.responses[q.id]?.answer);

  async function transition(action: "approve" | "return", note?: string) {
    setSaving(true);
    try {
      const res = await post<{ data: ScenarioDetail }>(`${path}/categories/${current.cat.id}/${action}`, { expected_version: currentState.version, ...(note ? { note } : {}) });
      qc.setQueryData(scenarioKey(institution.id, a.id, s.kind), res.data);
      await refresh();
      setReturning(false);
      setUnapprovedNotice(null);
      toast("success", action === "approve" ? "Categoria aprovada." : "Categoria devolvida ao avaliador.");
    } catch (e) {
      onError(e, "Não foi possível concluir.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="eyebrow">{SCENARIO_TAGLINE[s.kind]}</p>
          <h2 className="text-xl font-bold">{SCENARIO_LABEL[s.kind]}</h2>
          {s.kind === "prognosis" && <p className="mt-0.5 text-sm text-muted">{PROGNOSIS_NOTE}</p>}
        </div>
        <p className="num text-sm" aria-live="polite">{scores.answered} de {scores.total} quesitos respondidos</p>
      </div>
      {frozen && (
        <Alert tone="success" title={`${SCENARIO_LABEL[s.kind]} encerrado em ${formatInstantBR(s.finalized_at)}${s.finalized_by_name ? ` por ${s.finalized_by_name}` : ""}.`}
          action={s.kind === "diagnosis" && a.capabilities.can_start_prognosis
            ? <GuardedLink className="inline-flex min-h-10 items-center rounded-ctl bg-primary px-4 text-sm font-semibold text-white" href={withInstitution(`/diagnosticos/${a.id}/avaliacao/prognosis`, institution.id)}>Iniciar prognóstico</GuardedLink>
            : undefined}>
          As respostas estão preservadas e não podem ser alteradas. Para corrigir, duplique a avaliação na lista de avaliações.
        </Alert>
      )}
      {conflict && (
        <Alert tone="warn" title={CONFLICT_MESSAGE}
          action={<Button variant="secondary" onClick={() => { setConflict(false); void qc.invalidateQueries({ queryKey: scenarioKey(institution.id, a.id, s.kind) }); void refresh(); }}>Carregar versão atual (descarta suas alterações)</Button>}>
          Suas alterações não foram salvas.
        </Alert>
      )}
      {unapprovedNotice && unapprovedNotice.length > 0 && (
        <Alert tone="warn" title={`Faltam ${unapprovedNotice.length} ${unapprovedNotice.length === 1 ? "categoria atribuída" : "categorias atribuídas"} para aprovar.`}
          action={<Button variant="secondary" onClick={() => { setUrl({ cat: unapprovedNotice[0]! }); setUnapprovedNotice(null); }}>Ir para a primeira</Button>}>
          Quem encerra é o consultor: aprove cada categoria respondida pelos participantes antes de encerrar.
        </Alert>
      )}
      {pendingNotice !== null && (
        <Alert tone="warn" title={`Faltam ${pendingNotice} quesitos sem resposta.`} action={<Button variant="secondary" onClick={goToFirstPending}>Ir para o primeiro pendente</Button>}>
          Para encerrar, todos os quesitos precisam de resposta (Sim, Em parte ou Não).
        </Alert>
      )}
      <div className="grid gap-6 lg:grid-cols-[17rem_1fr]">
        <aside className="lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)] lg:self-start lg:overflow-y-auto">
          <div className="lg:hidden">
            <SelectField label="Categoria" value={current.cat.id} onChange={(e) => setUrl({ cat: e.target.value })}>
              {a.definition.dimension.subdimensions.map((sd) => (
                <optgroup key={sd.id} label={sd.title}>
                  {sd.categories.map((c) => { const sc = catScore(c); return <option key={c.id} value={c.id}>{c.code} · {sc.answered}/{sc.total}{assignments[c.id] ? ` · ${CATEGORY_STATE_LABEL[stateOf(c.id).state]}` : ""}</option>; })}
                </optgroup>
              ))}
            </SelectField>
          </div>
          <nav aria-label="Categorias do questionário" className="hidden lg:block">
            {a.definition.dimension.subdimensions.map((sd) => (
              <div key={sd.id} className="mb-3">
                <p className="eyebrow px-2 pb-1">{sd.code}</p>
                <ul className="space-y-0.5">
                  {sd.categories.map((c) => {
                    const sc = catScore(c);
                    const active = c.id === current.cat.id;
                    return (
                      <li key={c.id}>
                        <button type="button" onClick={() => setUrl({ cat: c.id })} aria-current={active ? "true" : undefined}
                          className={cx("flex w-full items-start gap-2 rounded-ctl px-2 py-1.5 text-left text-sm", active ? "bg-surface font-semibold text-primary shadow-e1 ring-1 ring-line" : "elevate text-muted hover:text-ink")}>
                          {assignments[c.id] && <StateDot state={stateOf(c.id)} />}
                          <span className="min-w-0 flex-1 truncate" title={c.title}>{c.title}</span>
                          <span className={cx("num shrink-0 text-xs", sc.answered === sc.total ? "text-soft-ink" : "text-ink-3")}>{sc.answered}/{sc.total}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>
        <section aria-labelledby="cat-title">
          <Panel className="mb-4">
            <p className="eyebrow">{current.sub.title}</p>
            <h3 id="cat-title" className="mt-1 text-[17px] font-semibold">{current.cat.title}</h3>
            <CategoryScoreLine score={cs} unsaved={dirty} />
            {currentAssignment && (
              <AssignmentBox assignment={currentAssignment} state={currentState} frozen={frozen} busy={saving}
                canApprove={categorySavedComplete && !categoryDirty} onApprove={() => void transition("approve")} onReturn={() => setReturning(true)} />
            )}
          </Panel>
          <ol className="space-y-3">
            {current.cat.questions.map((q) => (
              <QuestionCard key={q.id} q={q} entry={buffer[q.id] ?? EMPTY_ENTRY} frozen={frozen} onChange={(p) => setEntry(q.id, p)} assigned={Boolean(currentAssignment)}
                reference={reference ? (reference.responses[q.id]?.answer ?? null) : undefined} />
            ))}
          </ol>
          <div className="mt-4 flex flex-wrap justify-between gap-2">
            <Button variant="secondary" disabled={index === 0} onClick={() => setUrl({ cat: categories[index - 1]!.cat.id })}><ChevronLeft className="size-4" aria-hidden />Categoria anterior</Button>
            <Button variant="secondary" disabled={index === categories.length - 1} onClick={() => setUrl({ cat: categories[index + 1]!.cat.id })}>Próxima categoria<ChevronRight className="size-4" aria-hidden /></Button>
          </div>
        </section>
      </div>
      {!frozen && (
        <StickyActions dirty={dirty} onSave={() => void save()} summary={<span className="num text-muted">{scores.answered} de {scores.total} quesitos respondidos</span>}>
          <Button variant="secondary" loading={saving && !confirming} disabled={!dirty} onClick={() => void save()}>Salvar rascunho</Button>
          <Button onClick={askFinalize} disabled={saving}>{s.kind === "diagnosis" ? "Encerrar diagnóstico" : "Encerrar prognóstico"}</Button>
        </StickyActions>
      )}
      <ReturnDialog open={returning} busy={saving} who={currentAssignment?.member_name ?? ""} onClose={() => setReturning(false)} onConfirm={(note) => void transition("return", note)} />
      <Modal open={confirming} onOpenChange={(o) => !o && !saving && setConfirming(false)} title={s.kind === "diagnosis" ? "Encerrar diagnóstico" : "Encerrar prognóstico"}
        footer={<><Button variant="secondary" disabled={saving} onClick={() => setConfirming(false)}>Voltar</Button><Button loading={saving} onClick={() => void finalize()}>{s.kind === "diagnosis" ? "Encerrar diagnóstico" : "Encerrar prognóstico"}</Button></>}>
        <p className="text-sm">O {s.kind === "diagnosis" ? "diagnóstico" : "prognóstico"} encerrado será preservado. Para corrigir os dados posteriormente, crie uma nova avaliação baseada nesta.</p>
        {s.kind === "diagnosis" && <p className="mt-2 text-sm text-muted">O prognóstico é um cenário separado: depois de encerrar, você poderá iniciá-lo com as mesmas perguntas, em branco.</p>}
        {dirty && <p className="mt-2 text-sm text-muted">As respostas desta tela, inclusive as ainda não salvas, serão gravadas e encerradas juntas.</p>}
      </Modal>
    </div>
  );
}

function StateDot({ state }: { state: CategoryStateInfo }) {
  const color = { gray: "bg-ink-3", blue: "bg-primary", gold: "bg-[#B5541C]", green: "bg-soft-ink" }[CATEGORY_STATE_TONE[state.state]];
  return <span className={cx("mt-1.5 size-2 shrink-0 rounded-full", color)} title={CATEGORY_STATE_LABEL[state.state]}><span className="sr-only">{CATEGORY_STATE_LABEL[state.state]}</span></span>;
}

function AssignmentBox({ assignment: x, state, frozen, busy, canApprove, onApprove, onReturn }: {
  assignment: WorkflowAssignment; state: CategoryStateInfo; frozen: boolean; busy: boolean; canApprove: boolean; onApprove: () => void; onReturn: () => void;
}) {
  return (
    <div className="mt-3 rounded-ctl bg-paper px-3 py-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-1.5"><UserRound className="size-4 text-ink-3" aria-hidden />Responde: <strong>{x.member_name}</strong> ({x.group_name})</span>
        <span className="text-muted">Gestor: {x.manager_name ?? "sem gestor no grupo"}</span>
        <Pill tone={CATEGORY_STATE_TONE[state.state]}>{CATEGORY_STATE_LABEL[state.state]}</Pill>
      </div>
      {state.state === "returned" && state.note && <p className="mt-1.5 text-muted">Observação: {state.note}</p>}
      {!frozen && (
        <div className="mt-2 flex flex-wrap gap-2">
          {state.state !== "approved" && (
            <Button variant="secondary" disabled={!canApprove || busy} onClick={onApprove} title={canApprove ? undefined : "Responda e salve todos os quesitos da categoria"}>
              <CheckCircle2 className="size-4" aria-hidden />Aprovar categoria
            </Button>
          )}
          <Button variant="ghost" disabled={busy} onClick={onReturn}><Undo2 className="size-4" aria-hidden />Devolver ao avaliador</Button>
        </div>
      )}
      {!frozen && state.state !== "approved" && !canApprove && <p className="mt-1 text-xs text-ink-3">Para aprovar, todos os quesitos da categoria precisam estar respondidos e salvos.</p>}
    </div>
  );
}

function ReturnDialog({ open, busy, who, onClose, onConfirm }: { open: boolean; busy: boolean; who: string; onClose: () => void; onConfirm: (note: string) => void }) {
  const [note, setNote] = useState("");
  return (
    <Modal open={open} onOpenChange={(o) => !o && !busy && onClose()} title="Devolver ao avaliador"
      description={`${who} poderá ajustar as respostas e enviar de novo para validação.`}
      footer={<><Button variant="secondary" disabled={busy} onClick={onClose}>Cancelar</Button><Button loading={busy} disabled={!note.trim()} onClick={() => onConfirm(note.trim())}>Devolver</Button></>}>
      <TextArea label="O que precisa ser ajustado?" required maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} />
    </Modal>
  );
}

function CategoryScoreLine({ score, unsaved }: { score: ScoreSummary; unsaved: boolean }) {
  return (
    <p className="mt-2 flex flex-wrap items-center gap-2 text-sm">
      {score.fraction ? (
        <>
          <span className="num font-medium">{formatPercent(score)}</span>
          <span className="text-muted">· {score.adoption} · {score.situation}</span>
        </>
      ) : (
        <span className="text-muted">Pendente de preenchimento · <span className="num">{score.answered} de {score.total}</span> quesitos respondidos</span>
      )}
      {unsaved && <Pill tone="gold">Prévia não salva</Pill>}
    </p>
  );
}

function QuestionCard({ q, entry, frozen, onChange, reference, assigned }: {
  q: DefQuestion; entry: ResponseEntry; frozen: boolean; onChange: (p: Partial<ResponseEntry>) => void; reference: Answer | null | undefined; assigned: boolean;
}) {
  const hasNotes = Boolean(entry.evaluator_unit || entry.group_comment || entry.consultant_comment);
  const [open, setOpen] = useState(hasNotes);
  return (
    <li className="rounded-panel border border-line bg-surface p-4 shadow-e1">
      <fieldset>
        <legend className="w-full">
          <span className="eyebrow block">{q.code}</span>
          <span className="mt-0.5 block text-[15px] leading-snug">{q.text}</span>
        </legend>
        {reference !== undefined && (
          <p className="mt-2 text-xs text-muted">Diagnóstico: <span className="font-semibold text-ink">{reference ? ANSWER_LABEL[reference] : NO_ANSWER_LABEL}</span></p>
        )}
        <AnswerOptions name={`answer-${q.id}`} value={entry.answer} frozen={frozen} onChange={(v) => onChange({ answer: v })} />
      </fieldset>
      {frozen ? (
        hasNotes && (
          <dl className="mt-3 space-y-1 rounded-ctl bg-paper p-3 text-sm">
            {entry.evaluator_unit && <div><dt className="inline font-semibold">Unidade avaliadora: </dt><dd className="inline">{entry.evaluator_unit}</dd></div>}
            {entry.group_comment && <div><dt className="font-semibold">Comentário do grupo</dt><dd className="whitespace-pre-wrap">{entry.group_comment}</dd></div>}
            {entry.consultant_comment && <div><dt className="font-semibold">Comentário do consultor</dt><dd className="whitespace-pre-wrap">{entry.consultant_comment}</dd></div>}
          </dl>
        )
      ) : (
        <div className="mt-3">
          <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="elevate inline-flex min-h-9 items-center gap-1.5 rounded-ctl px-2 text-sm font-semibold text-primary">
            <MessageSquareText className="size-4" aria-hidden />
            {open ? "Ocultar comentários" : hasNotes ? "Comentários registrados" : "Adicionar unidade e comentários (opcional)"}
          </button>
          {open && (
            <div className="mt-1 rounded-ctl bg-paper px-3 pb-1">
              <p className="pt-2 text-xs text-muted">{assigned
                ? "A unidade e o comentário do grupo vêm do participante que responde esta categoria; o comentário do consultor é só seu e não aparece para os participantes."
                : "Registrados pelo instrutor a partir da discussão do grupo; não são respostas individuais."}</p>
              <TextField label="Unidade avaliadora" maxLength={MATURITY_LIMITS.label} value={entry.evaluator_unit ?? ""} onChange={(e) => onChange({ evaluator_unit: e.target.value || null })} />
              <TextArea label="Comentário do grupo" maxLength={MATURITY_LIMITS.comment} value={entry.group_comment ?? ""} onChange={(e) => onChange({ group_comment: e.target.value || null })} />
              <TextArea label="Comentário do consultor" maxLength={MATURITY_LIMITS.comment} value={entry.consultant_comment ?? ""} onChange={(e) => onChange({ consultant_comment: e.target.value || null })} />
            </div>
          )}
        </div>
      )}
    </li>
  );
}
